import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { withStyles, Typography } from '@material-ui/core'
import { track } from '@amplitude/analytics-browser'
import { useSelector, useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { PatchContent, PatchData } from 'src/model/patch'
import { patchCollectionSettingsAction } from 'src/reducers/collection/action'
import { sendCollectionTestEmailService } from 'src/api/collection'
import UserType from 'src/model/user-types'
import {
  WhiteLabelHeader,
  EmailProviderSelector,
  EmailSettings,
} from './components'
import { CoreButton } from 'src/components/core'
import styles from './styles'
import WhiteLabelBanner from 'src/components/white-label-banner'
import {
  DEFAULT_SMTP_PORT,
  EMAIL_PROVIDER,
  PROVIDER_LABELS,
  SMTP_PORTS,
  SMTP_SECURITY,
  SMTP_SECURITY_LABELS,
  SMTP_SECURITY_OPTIONS,
} from './constants'
import {
  describeApiError,
  emailDomain,
  findSmtpPreset,
  findSmtpPresetByEmail,
  isValidSmtpHost,
  parseSmtpHostInput,
  smtpPresetId,
} from './utils'

const emptySmtp = {
  host: '',
  port: DEFAULT_SMTP_PORT,
  username: '',
  password: '',
  security: SMTP_SECURITY.START_TLS,
}

// A collection that has never had a provider saved comes back with a null
// emailProvider; those start on SMTP. A stored 0 still means SendGrid.
const resolveProvider = settings =>
  settings?.emailProvider ?? EMAIL_PROVIDER.SMTP

const handleUserAgreement = () => {
  if (
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'Basic' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 1)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 2)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 3)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 1A)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 2A)' ||
    UserType[window.CONSTANTS.USER_ROLE_ID] === 'AppSumo (Tier 3A)'
  ) {
    return false
  }
  return true
}

const WhiteLabeling = ({ classes }) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const collection = useSelector(state => state.collection.collection)
  const collectionSettings = useSelector(
    state => state.collection.collectionSettings,
  )

  const [selectedProvider, setSelectedProvider] = useState(
    resolveProvider(collectionSettings),
  )
  const [apiToken, setApiToken] = useState(
    collectionSettings?.emailProviderApiKey || '',
  )
  const [sendingDomain, setSendingDomain] = useState(
    collectionSettings?.emailSenderAddress || '',
  )
  const [senderName, setSenderName] = useState(
    collectionSettings?.emailSenderName || '',
  )
  const [emailProviderEnabled, setEmailProviderEnabled] = useState(false)
  const [smtp, setSmtp] = useState(emptySmtp)
  // Once the provider has been picked by hand, the sending address stops steering it.
  const [smtpPresetTouched, setSmtpPresetTouched] = useState(false)
  // The saved SMTP password is never sent to the client; we only learn that one exists.
  const [smtpHasPassword, setSmtpHasPassword] = useState(false)
  const [testEmail, setTestEmail] = useState({
    sending: false,
    outcome: null,
    message: '',
  })

  useEffect(() => {
    if (collectionSettings) {
      setSelectedProvider(resolveProvider(collectionSettings))
      setApiToken(collectionSettings.emailProviderApiKey || '')
      setSendingDomain(collectionSettings.emailSenderAddress || '')
      setSenderName(collectionSettings.emailSenderName || '')
      setEmailProviderEnabled(collectionSettings.emailProviderEnabled || false)
      setSmtp({
        host: collectionSettings.smtpHost || '',
        port: collectionSettings.smtpPort || DEFAULT_SMTP_PORT,
        username: collectionSettings.smtpUsername || '',
        password: '',
        security: collectionSettings.smtpSecurity ?? SMTP_SECURITY.START_TLS,
      })
      setSmtpHasPassword(collectionSettings.smtpHasPassword || false)
    }
  }, [collectionSettings])

  const handleProviderChange = event => {
    const newProvider = parseInt(event.target.value)

    track('email_provider_selected', {
      collection_id: collection?.id,
      from_provider: PROVIDER_LABELS[selectedProvider],
      to_provider: PROVIDER_LABELS[newProvider],
    })

    setSelectedProvider(newProvider)
    // Clear fields when provider changes
    setApiToken('')
    setSendingDomain('')
    setSenderName('')
    setSmtp(emptySmtp)
    setSmtpPresetTouched(false)
  }

  const handleSmtpChange = (field, value) => {
    setSmtp(previous => {
      const next = { ...previous, [field]: value }

      // Follow the conventional port for the chosen transport security, unless the
      // user has already picked a port that differs from the previous default.
      if (field === 'security') {
        const previousDefault = SMTP_SECURITY_OPTIONS.find(
          option => option.value === previous.security,
        )?.defaultPort
        const nextDefault = SMTP_SECURITY_OPTIONS.find(
          option => option.value === value,
        )?.defaultPort

        if (nextDefault && previous.port === previousDefault) {
          next.port = nextDefault
        }
      }

      return next
    })
  }

  const applySmtpPreset = preset => {
    setSmtp(previous => ({
      ...previous,
      host: preset ? preset.host : '',
      port: preset ? preset.port : previous.port,
      security: preset ? preset.security : previous.security,
    }))
  }

  // Picking a known provider fills the whole connection; Other (a null preset)
  // clears the host and leaves the transport settings for the user to choose.
  const handleSmtpPresetSelect = preset => {
    setSmtpPresetTouched(true)
    applySmtpPreset(preset)
  }

  // A free consumer address names its own host, so answer the provider question
  // on the user's behalf. Custom domains resolve to nothing and stay untouched,
  // as does anything the user has already configured by hand.
  const handleSendingDomainChange = event => {
    const value = event.target.value
    setSendingDomain(value)

    if (selectedProvider !== EMAIL_PROVIDER.SMTP || smtpPresetTouched) return
    if (smtp.host && !findSmtpPreset(smtp.host)) return

    const preset = findSmtpPresetByEmail(value)
    if (preset && preset.host !== smtp.host) applySmtpPreset(preset)
  }

  // A pasted URL or host:port pair is normalised once the user leaves the field,
  // rather than on every keystroke, so typing a port is never fought mid-word.
  const handleSmtpHostBlur = () => {
    setSmtp(previous => {
      const { host, port } = parseSmtpHostInput(previous.host)
      const next = { ...previous, host }

      if (port && SMTP_PORTS.includes(port)) {
        next.port = port
        const matchingSecurity = SMTP_SECURITY_OPTIONS.find(
          option => option.defaultPort === port,
        )
        if (matchingSecurity) next.security = matchingSecurity.value
      }

      return next
    })
  }

  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Whether the provider on screen could actually send, independent of the
  // switch: turning white label on has to consult this before the state flips.
  const isProviderConfigured = useMemo(() => {
    // Sender identity is required by every provider
    if (!sendingDomain || !senderName) return false
    if (!isValidEmail(sendingDomain)) return false
    if (senderName.trim().length < 2) return false

    if (selectedProvider === EMAIL_PROVIDER.SMTP) {
      // Validate what the host would become once normalised, so a pasted URL does
      // not disable Save before the blur that cleans it up has landed.
      if (!isValidSmtpHost(parseSmtpHostInput(smtp.host).host)) return false
      if (!smtp.port) return false

      // A relay that takes credentials needs both halves; the password may already
      // be stored server-side, in which case a blank field keeps it.
      if (smtp.username && !smtp.password && !smtpHasPassword) return false

      return true
    }

    return !!apiToken && apiToken.length >= 10
  }, [
    apiToken,
    sendingDomain,
    senderName,
    selectedProvider,
    smtp,
    smtpHasPassword,
  ])

  // Nothing to validate while white label is off — the fields are inert then.
  const isFormValid = !emailProviderEnabled || isProviderConfigured

  // The shape of the configuration on screen, for analytics. Credentials are
  // reported as booleans; the host and the sending domain are the only values
  // that travel, and they are what separates one provider story from another.
  const trackingProps = useCallback(() => {
    const base = {
      collection_id: collection?.id,
      provider: PROVIDER_LABELS[selectedProvider],
      white_label_enabled: emailProviderEnabled,
      sender_domain: emailDomain(sendingDomain),
      has_sender_name: !!senderName.trim(),
    }

    if (selectedProvider !== EMAIL_PROVIDER.SMTP) {
      return { ...base, has_api_token: !!apiToken }
    }

    const host = parseSmtpHostInput(smtp.host).host

    return {
      ...base,
      smtp_host: host || null,
      smtp_port: smtp.port,
      smtp_security: SMTP_SECURITY_LABELS[smtp.security],
      smtp_preset: smtpPresetId(host),
      // Whether the sending address answered the provider question by itself,
      // which is the whole reason the presets exist.
      smtp_preset_autofilled: !smtpPresetTouched && !!findSmtpPreset(host),
      has_smtp_username: !!smtp.username,
      has_smtp_password: !!smtp.password || smtpHasPassword,
    }
  }, [
    apiToken,
    collection,
    emailProviderEnabled,
    selectedProvider,
    senderName,
    sendingDomain,
    smtp,
    smtpHasPassword,
    smtpPresetTouched,
  ])

  // The whole provider block, so the switch and the Save button write the same
  // thing: enabling must carry the credentials on screen, not just the flag.
  const buildSettingsPatch = enabled => {
    const patchSource = new PatchData(null, collection.id)
    patchSource.addContent(
      new PatchContent(
        selectedProvider,
        'emailProvider',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(
        apiToken,
        'emailProviderApiKey',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(
        sendingDomain,
        'emailSenderAddress',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(
        senderName,
        'emailSenderName',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(
        enabled,
        'emailProviderEnabled',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(
        parseSmtpHostInput(smtp.host).host,
        'smtpHost',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(smtp.port, 'smtpPort', PatchData.OPERATION_REPLACE),
    )
    patchSource.addContent(
      new PatchContent(
        smtp.username,
        'smtpUsername',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(
        smtp.security,
        'smtpSecurity',
        PatchData.OPERATION_REPLACE,
      ),
    )
    // An empty password tells the backend to keep whatever is already stored.
    patchSource.addContent(
      new PatchContent(
        smtp.password,
        'smtpPassword',
        PatchData.OPERATION_REPLACE,
      ),
    )

    return patchSource
  }

  const onSettingsSaved = message => {
    enqueueSnackbar(message, { variant: 'success' })

    if (collectionSettings) {
      setSelectedProvider(resolveProvider(collectionSettings))
      setApiToken(collectionSettings.emailProviderApiKey || '')
      setSendingDomain(collectionSettings.emailSenderAddress || '')
      setSenderName(collectionSettings.emailSenderName || '')
      setEmailProviderEnabled(collectionSettings.emailProviderEnabled || false)
    }

    // Never keep the typed password in memory after it has been saved.
    if (smtp.password) {
      setSmtpHasPassword(true)
      setSmtp(previous => ({ ...previous, password: '' }))
    }
  }

  const handleSaveSettings = () => {
    if (!isFormValid) return

    // Captured before the write, so the success and failure events describe the
    // configuration that was actually submitted.
    const properties = trackingProps()
    track('email_settings_save_clicked', properties)

    dispatch(
      patchCollectionSettingsAction(
        buildSettingsPatch(emailProviderEnabled),
        () => {
          track('email_settings_save_result', {
            ...properties,
            outcome: 'success',
          })
          onSettingsSaved('Email settings saved')
        },
        error => {
          track('email_settings_save_result', {
            ...properties,
            outcome: 'error',
            ...describeApiError(error),
          })
        },
      ),
    )
  }

  const handleEmailProviderToggle = () => {
    const newState = !emailProviderEnabled
    setEmailProviderEnabled(newState)

    // The switch reports the state it was moved to, and only once its outcome is
    // known, so a flip that got rolled back is never counted as a success.
    const properties = {
      ...trackingProps(),
      // Both describe the state being moved to: the shared property so this
      // event lines up with the others, `enabled` as the switch's own reading.
      white_label_enabled: newState,
      enabled: newState,
    }
    const trackToggle = (outcome, error) =>
      track('white_label_email_toggled', {
        ...properties,
        outcome,
        ...(error ? describeApiError(error) : {}),
      })

    // The switch is the setting, so both directions reach the server on their
    // own. If the write is rejected it is put back, rather than leaving the
    // screen claiming something the collection is not doing.
    const revert = error => {
      setEmailProviderEnabled(!newState)
      trackToggle('error', error)
    }

    if (!newState) {
      const patchSource = new PatchData(null, collection.id)
      patchSource.addContent(
        new PatchContent(
          false,
          'emailProviderEnabled',
          PatchData.OPERATION_REPLACE,
        ),
      )

      dispatch(
        patchCollectionSettingsAction(
          patchSource,
          () => {
            trackToggle('success')
            enqueueSnackbar('White label email turned off', {
              variant: 'success',
            })
          },
          revert,
        ),
      )
      return
    }

    // Turning on with nothing configured yet: the fields only become editable
    // once the switch is on, so open them and let Save carry the credentials.
    // Enabling an empty provider server-side would just break every send.
    if (!isProviderConfigured) {
      trackToggle('needs_setup')
      enqueueSnackbar(
        'Add your provider details and press Save settings to finish turning white label on.',
        { variant: 'info' },
      )
      return
    }

    dispatch(
      patchCollectionSettingsAction(
        buildSettingsPatch(true),
        () => {
          trackToggle('success')
          onSettingsSaved('White label email turned on')
        },
        revert,
      ),
    )
  }

  // The test uses the configuration stored on the server, so it is only meaningful
  // once the provider has actually been saved.
  const canSendTestEmail = !!collectionSettings?.emailProviderEnabled

  const handleSendTestEmail = async () => {
    // The test runs against the saved configuration, which is what is on screen
    // once a save has landed, and the button is only live after one has.
    const properties = trackingProps()
    const startedAt = performance.now()
    const elapsed = () => Math.round(performance.now() - startedAt)

    track('test_email_sent', properties)
    setTestEmail({ sending: true, outcome: null, message: '' })

    try {
      await sendCollectionTestEmailService(collection.id)
      track('test_email_result', {
        ...properties,
        outcome: 'success',
        duration_ms: elapsed(),
      })
      setTestEmail({
        sending: false,
        outcome: 'success',
        message: 'Test email sent. Check the inbox of your account address.',
      })
    } catch (error) {
      track('test_email_result', {
        ...properties,
        outcome: 'error',
        duration_ms: elapsed(),
        ...describeApiError(error),
      })
      setTestEmail({
        sending: false,
        outcome: 'error',
        message:
          error?.message ||
          'Could not send the test email. Check the settings above and try again.',
      })
    }
  }

  const userControl = handleUserAgreement()

  return (
    <div className={classes.container}>
      <WhiteLabelHeader
        userControl={userControl}
        emailProviderEnabled={emailProviderEnabled}
        onToggle={handleEmailProviderToggle}
      />

      {userControl ? (
        <>
          <div className={emailProviderEnabled ? '' : classes.disabledContent}>
            <EmailProviderSelector
              selectedProvider={selectedProvider}
              onProviderChange={handleProviderChange}
            />
            <EmailSettings
              provider={selectedProvider}
              apiToken={apiToken}
              onApiTokenChange={e => setApiToken(e.target.value)}
              sendingDomain={sendingDomain}
              onSendingDomainChange={handleSendingDomainChange}
              senderName={senderName}
              onSenderNameChange={e => setSenderName(e.target.value)}
              isValidEmail={isValidEmail}
              smtp={smtp}
              onSmtpChange={handleSmtpChange}
              onSmtpHostBlur={handleSmtpHostBlur}
              onSmtpPresetSelect={handleSmtpPresetSelect}
              smtpHasPassword={smtpHasPassword}
            />
          </div>
          <div className={classes.actions}>
            <CoreButton
              variant="contained"
              color="primary"
              onClick={handleSaveSettings}
              disabled={!isFormValid}
              className={classes.saveButton}
            >
              Save settings
            </CoreButton>
            <CoreButton
              variant="outlined"
              color="primary"
              onClick={handleSendTestEmail}
              disabled={!canSendTestEmail || testEmail.sending}
              className={classes.saveButton}
            >
              {testEmail.sending ? 'Sending…' : 'Send test email'}
            </CoreButton>
          </div>
          {testEmail.outcome && (
            <Typography
              className={
                testEmail.outcome === 'success'
                  ? classes.testEmailSuccess
                  : classes.testEmailError
              }
            >
              {testEmail.message}
            </Typography>
          )}
        </>
      ) : (
        <WhiteLabelBanner />
      )}
    </div>
  )
}

export default withStyles(styles)(WhiteLabeling)
